import { NotificationChannel } from '../enums.js';
import type {
  NotificationTemplateDef,
  NotificationTemplateVariable,
} from '../types/notification-template.js';

/**
 * Registry of every configurable notification template.
 *
 * The `defaults` for each key reproduce the wording that used to be hard-coded
 * at the call site / in email-templates.ts, so seeding these defaults leaves
 * the system behaving exactly as before. Admins edit content in the admin UI;
 * unset content falls back to these defaults.
 *
 * Email `body` here is the *inner* HTML content only — the shared email shell
 * (branding + footer) is added by the backend at send time (wrapEmailHtml), so
 * admins never have to re-author the chrome. Structured emails reference
 * {{block:...}} tokens for tables/history that are still generated in code.
 */

// ── Default-HTML helpers (email inner content) ───────────────────
const H2 = (text: string) => `<h2 style="margin:0 0 12px;font-size:18px;">${text}</h2>`;
const PARA = (text: string) =>
  `<p style="margin:0 0 16px;font-size:14px;line-height:1.5;">${text}</p>`;
const BTN = (label: string, href = '{{link}}') =>
  `<a href="${href}" style="display:inline-block;padding:10px 20px;background-color:#3b82f6;color:#ffffff;text-decoration:none;border-radius:6px;font-size:14px;">${label}</a>`;

/** Standard email body: heading + paragraph + "View Details" button. */
const emailBody = (heading: string, paragraph: string, btnLabel = 'View Details') =>
  `${H2(heading)}\n${PARA(paragraph)}\n${BTN(btnLabel)}`;

// ── Common variable presets ──────────────────────────────────────
const V = {
  link: {
    token: 'link',
    label: 'Deep link',
    description: 'App URL to the related record (used by the email button).',
    example: 'https://app.merkos302.com/expenses',
  } as NotificationTemplateVariable,
  actor: {
    token: 'actor_name',
    label: 'Actor',
    description: 'Name of the user who performed the action.',
    example: 'Mendel Cohen',
  } as NotificationTemplateVariable,
  dept: {
    token: 'department',
    label: 'Department',
    example: 'Education',
  } as NotificationTemplateVariable,
  amount: {
    token: 'amount',
    label: 'Amount',
    format: 'currency',
    example: '1250',
  } as NotificationTemplateVariable,
  label: {
    token: 'label',
    label: 'Record label',
    description: 'Short human label for the record (description / name).',
    example: 'Office supplies — Staples',
  } as NotificationTemplateVariable,
};

const IN_APP = NotificationChannel.InApp;
const EMAIL = NotificationChannel.Email;

/**
 * Payee "expense paid" emails — one template per way of paying, so the wording
 * for each method (check for pickup, mailed check, transfer, …) is editable.
 * The caller picks the key (`payeePaidTemplateKey` in the backend) and passes
 * only data: names, amount, address, last four digits, PayPal email, bank.
 */
export const PAYEE_EXPENSE_PAID_KEYS = {
  generic: 'payee_expense_paid',
  checkPickup: 'payee_expense_paid_check_pickup',
  checkMail: 'payee_expense_paid_check_mail',
  transfer: 'payee_expense_paid_transfer',
  paypal: 'payee_expense_paid_paypal',
  remittance: 'payee_expense_paid_remittance',
  payroll: 'payee_expense_paid_payroll',
} as const;

const PAYEE_PAID_VARIABLES = [
  { token: 'payee_name', label: 'Payee name', example: 'Staples Inc.' },
  { ...V.link, description: 'App URL to the paid expense (used by the email button).' },
  { token: 'expense_label', label: 'Expense label', example: 'Q3 office supplies' },
  V.amount,
  { token: 'payment_method', label: 'Payment method', example: 'Check' },
  { token: 'mailing_address', label: 'Mailing address', description: 'Where a mailed check was sent (blank when unknown).', example: '1 Main St, Brooklyn, NY 11213' },
  { token: 'account_last4', label: 'Account last 4', description: 'Last four digits of the receiving account (blank when unknown).', example: '6789' },
  { token: 'paypal_email', label: 'PayPal email', example: 'billing@staples.test' },
  { token: 'bank_name', label: 'Bank name', description: 'Bank of the enrolled account (electronic payment).', example: 'Chase' },
  { token: 'rail', label: 'Electronic rail', description: 'ACH or international wire (electronic payment).', example: 'ACH' },
];

const payeePaid = (
  key: string,
  label: string,
  when: string,
  subject: string,
  methodHtml: string,
): NotificationTemplateDef => ({
  key,
  label,
  description: `Sent from the accounting email address to the payee (vendor or employee, external email), with the expense's creator copied, when the expense is paid ${when}.`,
  category: 'expenses',
  channels: [EMAIL],
  variables: PAYEE_PAID_VARIABLES,
  defaults: {
    email: {
      subject,
      body: `${H2('Payment Processed')}\n${PARA('Hello {{payee_name}},')}\n${PARA('Your payment of <strong>{{amount|currency}}</strong> for <strong>{{expense_label}}</strong> has been processed.')}\n${PARA(methodHtml)}\n${PARA(BTN('View Expense'))}\n<p style="font-size:13px;color:#6b7280;">If you have any questions about this payment, please contact our accounting team.</p>`,
    },
  },
});

const PAYEE_EXPENSE_PAID_TEMPLATES: NotificationTemplateDef[] = [
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.checkPickup, 'Payee — Paid: Check for Pickup', 'by a check to be picked up',
    'Your check is ready for pickup — {{amount|currency}}',
    '<strong>Payment method:</strong> Check<br>Your check is ready for pickup at our office.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.checkMail, 'Payee — Paid: Check by Mail', 'by a mailed check',
    'Your check was mailed — {{amount|currency}}',
    '<strong>Payment method:</strong> Check<br>Your check was sent by mail to {{mailing_address||the address we have on file}}. Please allow several business days for delivery.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.transfer, 'Payee — Paid: Bank Transfer', 'by bank transfer',
    'Payment sent — {{amount|currency}}',
    '<strong>Payment method:</strong> Bank transfer<br>The payment was sent to your bank account on file (ending in {{account_last4||—}}). Please allow 1–3 business days for it to arrive.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.paypal, 'Payee — Paid: PayPal', 'through PayPal',
    'Payment sent — {{amount|currency}}',
    '<strong>Payment method:</strong> PayPal<br>The payment was sent to {{paypal_email||your PayPal account}}.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.remittance, 'Payee — Paid: Electronic Payment', 'by electronic payment (Mercury remittance)',
    'Payment sent — {{amount|currency}}',
    '<strong>Payment method:</strong> Electronic payment ({{rail||ACH}})<br>The payment was sent to your enrolled bank account ({{bank_name||your bank}}, ending in {{account_last4||—}}). Please allow 1–3 business days for it to arrive.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.payroll, 'Payee — Paid: Payroll', 'through payroll',
    'Payment processed — {{amount|currency}}',
    '<strong>Payment method:</strong> Payroll<br>This payment is included in your payroll.'),
  payeePaid(PAYEE_EXPENSE_PAID_KEYS.generic, 'Payee — Paid: Other Method', 'by any other method (not credit card)',
    'Payment sent — {{amount|currency}}',
    '<strong>Payment method:</strong> {{payment_method}}'),
];

export const NOTIFICATION_TEMPLATES: NotificationTemplateDef[] = [
  // ── Expenses ───────────────────────────────────────────────────
  {
    key: 'expense_approved',
    label: 'Expense Approved',
    description: 'Sent to the submitter when their expense is approved.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [V.label, V.amount, V.link],
    defaults: {
      in_app: {
        subject: 'Expense Approved',
        body: 'Your expense "{{label}} ({{amount|currency}})" has been approved.',
      },
      email: {
        subject: 'Expense Approved',
        body: emailBody(
          'Expense Approved',
          'Your expense "{{label}} ({{amount|currency}})" has been approved.',
        ),
      },
    },
  },
  {
    key: 'expense_rejected',
    label: 'Expense Rejected',
    description: 'Sent to the submitter when their expense is rejected.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      V.label,
      V.amount,
      V.link,
      { token: 'note_suffix', label: 'Note line', description: 'Reviewer note, prefixed with " Note:" (blank when none).', example: ' Note: Missing receipt' },
    ],
    defaults: {
      in_app: {
        subject: 'Expense Rejected',
        body: 'Your expense "{{label}} ({{amount|currency}})" has been rejected.{{note_suffix}}',
      },
      email: {
        subject: 'Expense Rejected',
        body: emailBody(
          'Expense Rejected',
          'Your expense "{{label}} ({{amount|currency}})" has been rejected.{{note_suffix}}',
        ),
      },
    },
  },
  {
    key: 'expense_paid',
    label: 'Expense Paid',
    description: 'Sent to the submitter when their expense is marked paid.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [V.label, V.amount, V.link],
    defaults: {
      in_app: {
        subject: 'Expense Paid',
        body: 'Your expense "{{label}} ({{amount|currency}})" has been marked as paid.',
      },
      email: {
        subject: 'Expense Paid',
        body: emailBody(
          'Expense Paid',
          'Your expense "{{label}} ({{amount|currency}})" has been marked as paid.',
        ),
      },
    },
  },
  {
    key: 'remittance_paid',
    label: 'Direct Deposit Paid',
    description: 'No longer sent. A settled direct deposit marks its expenses paid, and each paid expense sends the payee payment confirmation (from the accounting address, the creator copied).',
    category: 'finance',
    channels: [IN_APP, EMAIL],
    variables: [V.amount, V.link, { token: 'target_account_name', label: 'Target account', example: 'Leah Katz' }],
    defaults: {
      in_app: {
        subject: 'Payment sent ({{amount|currency}})',
        body: 'A payment of {{amount|currency}} was sent to {{target_account_name}}.',
      },
      email: {
        subject: 'Payment sent ({{amount|currency}})',
        body: emailBody(
          'Payment sent',
          'A payment of {{amount|currency}} was sent to {{target_account_name}}.',
        ),
      },
    },
  },
  {
    key: 'remittance_failed',
    label: 'Direct Deposit Failed',
    description: 'Sent to an employee target_account when a direct deposit fails or is returned.',
    category: 'finance',
    channels: [IN_APP, EMAIL],
    variables: [
      V.amount,
      V.link,
      { token: 'target_account_name', label: 'Target account', example: 'Leah Katz' },
      { token: 'reason', label: 'Failure reason', example: 'returned' },
    ],
    defaults: {
      in_app: {
        subject: 'Payment failed ({{amount|currency}})',
        body: 'A payment of {{amount|currency}} to {{target_account_name}} failed.{{reason}}',
      },
      email: {
        subject: 'Payment failed ({{amount|currency}})',
        body: emailBody(
          'Payment failed',
          'A payment of {{amount|currency}} to {{target_account_name}} failed. {{reason}}',
        ),
      },
    },
  },
  {
    key: 'remittance_batch_completed',
    label: 'Direct Deposit Batch Completed',
    description: 'Sent to the finance notification group when a batch finishes submitting to Mercury.',
    category: 'finance',
    channels: [IN_APP, EMAIL],
    variables: [
      V.amount,
      V.link,
      { token: 'line_count', label: 'Line count', format: 'number', example: '4' },
      { token: 'failed_count', label: 'Failed lines', format: 'number', example: '1' },
      { token: 'status', label: 'Batch status', example: 'completed_with_errors' },
    ],
    defaults: {
      in_app: {
        subject: 'Direct deposit batch {{status}}',
        body: 'Submitted {{line_count}} payment(s) totaling {{amount|currency}}. {{failed_count}} failed.',
      },
      email: {
        subject: 'Direct deposit batch {{status}}',
        body: emailBody(
          'Direct deposit batch {{status}}',
          'Submitted {{line_count}} payment(s) totaling {{amount|currency}}. {{failed_count}} failed.',
        ),
      },
    },
  },
  {
    key: 'expense_request',
    label: 'Expense Request (department)',
    description: 'Sent to a department notification group when a new expense request is created.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'creator', label: 'Created by', example: 'Leah Katz' },
      V.label, V.amount, V.dept, V.link,
      { token: 'details_table', label: 'Expense details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'New Expense Request',
        body: '{{creator}} created expense request "{{label}}" ({{amount|currency}}) in {{department}}.',
      },
      email: {
        subject: 'New expense awaiting submission: {{label}}',
        body: `${H2('New expense awaiting submission: {{label}}')}\n<p style="margin:0 0 16px;font-size:14px;color:#6b7280;">A new expense was created and requires submission for review.</p>\n{{block:details_table}}\n${BTN('Review Expense')}`,
      },
    },
  },
  {
    key: 'expense_submitted',
    label: 'Expense Submitted for Review',
    description: 'Sent to the accounting notification group when an expense is submitted for review.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      V.label, V.amount, V.link,
      { token: 'vendor_diff_note', label: 'Vendor-diff note', description: 'Trailing sentence when payment details differ from the vendor record (blank otherwise).', example: '' },
      { token: 'subject_line', label: 'Email subject line', example: 'Education Submitted expense for: Staples' },
      { token: 'details_table', label: 'Expense details table', block: true },
      { token: 'vendor_diff', label: 'Vendor-diff callout', block: true },
      { token: 'attachments', label: 'Attachments list', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Expense Submitted for Review',
        body: 'Expense "{{label}}" ({{amount|currency}}) was submitted for accounting review.{{vendor_diff_note}}',
      },
      email: {
        subject: '{{subject_line}}',
        body: `${H2('{{subject_line}}')}\n{{block:details_table}}\n{{block:vendor_diff}}\n{{block:attachments}}\n${BTN('See Details')}`,
      },
    },
  },
  ...PAYEE_EXPENSE_PAID_TEMPLATES,

  // ── Budgets ────────────────────────────────────────────────────
  {
    key: 'budget_submitted',
    label: 'Budget Submitted',
    description: 'Sent to the approval notification group when a budget is submitted.',
    category: 'budgets',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'submitter', label: 'Submitted by', example: 'Rivka Stern' },
      { token: 'budget_label', label: 'Budget label', example: 'FY26 Education' },
      V.amount, V.dept,
      { token: 'scope', label: 'Scope', example: 'Summer Camp' },
      V.link,
      { token: 'details_table', label: 'Budget details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Budget Submitted for Approval',
        body: '{{submitter}} submitted "{{budget_label}}" ({{amount|currency}}) for approval — {{department}} / {{scope}}.',
      },
      email: {
        subject: 'Budget Submitted for Approval: {{budget_label}}',
        body: `${H2('Budget Submitted for Approval: {{budget_label}}')}\n{{block:details_table}}\n${BTN('View Budget')}`,
      },
    },
  },
  {
    key: 'budget_approved',
    label: 'Budget Approved',
    description: 'Sent to the department notification group when a budget is approved.',
    category: 'budgets',
    channels: [IN_APP, EMAIL],
    variables: [
      V.actor,
      { token: 'budget_label', label: 'Budget label', example: 'FY26 Education' },
      V.amount, V.dept, { token: 'scope', label: 'Scope', example: 'Summer Camp' },
      { token: 'note_suffix', label: 'Note line', example: '' },
      V.link,
      { token: 'details_table', label: 'Budget details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Budget Approved',
        body: '{{actor_name}} approved "{{budget_label}}" ({{amount|currency}}) — {{department}} / {{scope}}.{{note_suffix}}',
      },
      email: {
        subject: 'Budget Approved: {{budget_label}}',
        body: `${H2('Budget Approved: {{budget_label}}')}\n{{block:details_table}}\n${BTN('View Budget')}`,
      },
    },
  },
  {
    key: 'budget_rejected',
    label: 'Budget Rejected',
    description: 'Sent to the department notification group when a budget is rejected.',
    category: 'budgets',
    channels: [IN_APP, EMAIL],
    variables: [
      V.actor,
      { token: 'budget_label', label: 'Budget label', example: 'FY26 Education' },
      V.amount, V.dept, { token: 'scope', label: 'Scope', example: 'Summer Camp' },
      { token: 'note_suffix', label: 'Note line', example: ' Note: Over cap' },
      V.link,
      { token: 'details_table', label: 'Budget details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Budget Rejected',
        body: '{{actor_name}} rejected "{{budget_label}}" ({{amount|currency}}) — {{department}} / {{scope}}.{{note_suffix}}',
      },
      email: {
        subject: 'Budget Rejected: {{budget_label}}',
        body: `${H2('Budget Rejected: {{budget_label}}')}\n{{block:details_table}}\n${BTN('View Budget')}`,
      },
    },
  },
  {
    key: 'budget_returned_to_draft',
    label: 'Budget Moved Back to Draft',
    description: 'Sent when a submitted budget is returned to draft.',
    category: 'budgets',
    channels: [IN_APP, EMAIL],
    variables: [
      V.actor,
      { token: 'budget_label', label: 'Budget label', example: 'FY26 Education' },
      V.amount, V.dept, { token: 'scope', label: 'Scope', example: 'Summer Camp' },
      { token: 'note_suffix', label: 'Note line', example: '' },
      V.link,
      { token: 'details_table', label: 'Budget details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Budget Moved Back to Draft',
        body: '{{actor_name}} moved "{{budget_label}}" ({{amount|currency}}) back to draft — {{department}} / {{scope}}.{{note_suffix}}',
      },
      email: {
        subject: 'Budget Moved Back to Draft: {{budget_label}}',
        body: `${H2('Budget Moved Back to Draft: {{budget_label}}')}\n{{block:details_table}}\n${BTN('View Budget')}`,
      },
    },
  },

  // ── Subscriptions ──────────────────────────────────────────────
  {
    key: 'subscription_request',
    label: 'Subscription Request (department)',
    description: 'Sent to a department notification group for a new subscription request.',
    category: 'subscriptions',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'creator', label: 'Created by', example: 'Leah Katz' },
      { token: 'vendor', label: 'Vendor', example: 'Zoom' },
      V.amount,
      { token: 'recurring_type', label: 'Recurrence', example: 'monthly' },
      V.dept, V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New Subscription Request',
        body: '{{creator}} requested subscription "{{vendor}}" ({{amount|currency}} {{recurring_type}}) in {{department}}.',
      },
      email: {
        subject: 'New Subscription Request',
        body: emailBody('New Subscription Request', '{{creator}} requested subscription "{{vendor}}" ({{amount|currency}} {{recurring_type}}) in {{department}}.'),
      },
    },
  },
  {
    key: 'subscription_submitted',
    label: 'Subscription Submitted',
    description: 'Sent to the global notification group when a subscription is submitted.',
    category: 'subscriptions',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'creator', label: 'Created by', example: 'Leah Katz' },
      { token: 'vendor', label: 'Vendor', example: 'Zoom' },
      V.amount,
      { token: 'recurring_type', label: 'Recurrence', example: 'monthly' },
      V.dept, V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Subscription Submitted',
        body: '{{creator}} requested subscription "{{vendor}}" ({{amount|currency}} {{recurring_type}}) in {{department}}.',
      },
      email: {
        subject: 'Subscription Submitted',
        body: emailBody('Subscription Submitted', '{{creator}} requested subscription "{{vendor}}" ({{amount|currency}} {{recurring_type}}) in {{department}}.'),
      },
    },
  },

  // ── Threads ────────────────────────────────────────────────────
  {
    // In-app only. The @-mention EMAIL is highly stylized (per-sender colored
    // bubbles + conversation history) and stays on its dedicated builder
    // (threadMentionEmail); only the in-app row is configurable here.
    key: 'thread_mention',
    label: 'Thread Mention',
    description: 'In-app notification when a participant is @-mentioned in an inquiry thread.',
    category: 'threads',
    channels: [IN_APP],
    variables: [
      { token: 'sender', label: 'Sender', example: 'Yosef Klein' },
      { token: 'subject', label: 'Thread subject', example: 'Reimbursement question' },
    ],
    defaults: {
      in_app: {
        subject: '{{sender}} mentioned you',
        body: '{{sender}} mentioned you in "{{subject||an inquiry}}"',
      },
    },
  },
  {
    key: 'thread_status_changed',
    label: 'Thread Status Changed',
    description: 'Sent to the thread owner when their inquiry status changes.',
    category: 'threads',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'status_label', label: 'Status label', example: 'resolved' },
      { token: 'subject', label: 'Thread subject', example: 'Reimbursement question' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Your inquiry was {{status_label}}',
        body: '"{{subject}}" was {{status_label}}.',
      },
      email: {
        subject: 'Your inquiry was {{status_label}}',
        body: emailBody('Your inquiry was {{status_label}}', '"{{subject}}" was {{status_label}}.', 'View in App'),
      },
    },
  },
  // NOTE: the thread new-message email (threadMessageEmail) uses per-sender
  // colored bubbles + conversation history and stays on its dedicated builder;
  // it is not a configurable template.

  {
    key: 'feedback_submitted',
    label: 'Feedback Submitted',
    description: 'Sent to the feedback notification group for a bug report / feature request.',
    category: 'threads',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'type_label', label: 'Feedback type', example: 'Bug Report' },
      { token: 'sender', label: 'Sender', example: 'Chana Weiss' },
      { token: 'subject', label: 'Subject', example: 'Export button broken' },
      { token: 'message', label: 'Message body', example: 'Clicking export does nothing.' },
      { token: 'feedback_link', label: 'Feedback link', example: 'https://app.merkos302.com/admin/feedback' },
    ],
    defaults: {
      in_app: {
        subject: '{{type_label}}: {{subject}}',
        body: '{{message}}',
      },
      email: {
        subject: '[{{type_label}}] {{subject}}',
        body: `${H2('[{{type_label}}] {{subject}}')}\n<div style="background:#fef3c7;border-left:3px solid #f59e0b;padding:12px 16px;margin:0 0 8px;border-radius:4px;"><p style="margin:0;font-size:12px;font-weight:600;color:#92400e;">{{type_label}}</p></div>\n<div style="background:#f9fafb;border-left:3px solid #3b82f6;padding:12px 16px;margin:0 0 16px;border-radius:4px;"><p style="margin:0 0 4px;font-size:13px;font-weight:600;color:#6b7280;">{{sender}}</p><p style="margin:0;font-size:14px;line-height:1.5;">{{message}}</p></div>\n${BTN('View Feedback', '{{feedback_link}}')}`,
      },
    },
  },

  // ── Time tracking ──────────────────────────────────────────────
  {
    key: 'time_warning',
    label: 'Time Threshold Exceeded',
    description: 'Sent to a director/manager when an employee exceeds a time threshold.',
    category: 'time',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      { token: 'alerts', label: 'Alert details', description: 'Joined alert messages.', example: 'Over 40h this week' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Time threshold exceeded: {{employee_name}}',
        body: '{{alerts}}',
      },
      email: {
        subject: 'Time threshold exceeded: {{employee_name}}',
        body: emailBody('Time threshold exceeded: {{employee_name}}', '{{alerts}}', 'Review Time Entries'),
      },
    },
  },
  {
    key: 'time_edited',
    label: 'Time Entry Edited',
    description: 'Sent to an employee when their time entry is edited by someone else.',
    category: 'time',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'editor_name', label: 'Editor', example: 'Sarah Blum' },
      { token: 'date', label: 'Entry date', format: 'date', example: '2026-07-10' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Your time entry was edited by {{editor_name}}',
        body: 'Time entry on {{date|date}} was modified.',
      },
      email: {
        subject: 'Your time entry was edited by {{editor_name}}',
        body: emailBody('Your time entry was edited', 'Time entry on {{date|date}} was modified by {{editor_name}}.', 'View Time Entry'),
      },
    },
  },
  {
    key: 'time_submitted',
    label: 'Time Entries Submitted',
    description: 'Sent to an employee when their time entries are submitted by someone else.',
    category: 'time',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'submitter_name', label: 'Submitter', example: 'Sarah Blum' },
      { token: 'count_label', label: 'Count label', example: '3 time entries' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Your time entries were submitted by {{submitter_name}}',
        body: '{{count_label}} submitted for approval.',
      },
      email: {
        subject: 'Your time entries were submitted by {{submitter_name}}',
        body: emailBody('Your time entries were submitted', '{{count_label}} submitted for approval by {{submitter_name}}.', 'View Time Entries'),
      },
    },
  },
  {
    key: 'payroll_reminder',
    label: 'Payroll Reminder',
    description: 'Reminder that employees have unsubmitted time entries for a payroll period.',
    category: 'time',
    channels: [EMAIL],
    variables: [
      { token: 'employee_count', label: 'Employee count', format: 'number', example: '4' },
      { token: 'payroll_description', label: 'Payroll period', example: 'July 1–15' },
      V.link,
      { token: 'summary_table', label: 'Summary table', block: true },
      { token: 'employee_breakdown', label: 'Per-employee breakdown table', block: true },
      { token: 'project_breakdown', label: 'Per-project breakdown table', block: true },
    ],
    defaults: {
      email: {
        subject: 'Payroll reminder: {{employee_count}} employees have unsubmitted time entries',
        body: `${H2('&#128337; Payroll Reminder')}\n<p style="margin:0 0 16px;font-size:14px;color:#374151;">The following payroll period has employees with unsubmitted time entries. Please ensure your team submits their time before the deadline.</p>\n{{block:summary_table}}\n{{block:employee_breakdown}}\n{{block:project_breakdown}}\n${BTN('Review Team Entries')}\n<p style="margin:16px 0 0;font-size:13px;font-weight:600;color:#b45309;">Any hours that are not submitted will not be paid in the current payroll.</p>`,
      },
    },
  },

  // ── Fundraising ────────────────────────────────────────────────
  {
    key: 'lead_created',
    label: 'Fundraising Lead Created',
    description: 'Sent to the fundraising notification group for a new lead.',
    category: 'fundraising',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'creator', label: 'Created by', example: 'Leah Katz' },
      { token: 'lead_name', label: 'Lead name', example: 'Acme Foundation' },
      V.amount,
      { token: 'plan', label: 'Target plan', example: 'FY26 Campaign' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New Fundraising Lead',
        body: '{{creator}} added lead "{{lead_name}}" ({{amount|currency}}) for {{plan}}.',
      },
      email: {
        subject: 'New Fundraising Lead',
        body: emailBody('New Fundraising Lead', '{{creator}} added lead "{{lead_name}}" ({{amount|currency}}) for {{plan}}.'),
      },
    },
  },
  {
    key: 'pledge_created',
    label: 'Pledge Created',
    description: 'Sent to the fundraising notification group for a new pledge.',
    category: 'fundraising',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'creator', label: 'Created by', example: 'Leah Katz' },
      V.amount,
      { token: 'plan', label: 'Target plan', example: 'FY26 Campaign' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New Pledge',
        body: '{{creator}} recorded a {{amount|currency}} pledge for {{plan}}.',
      },
      email: {
        subject: 'New Pledge',
        body: emailBody('New Pledge', '{{creator}} recorded a {{amount|currency}} pledge for {{plan}}.'),
      },
    },
  },
  {
    key: 'pledge_received',
    label: 'Pledge Received (group)',
    description: 'Sent to the fundraising notification group when a pledge payment is recorded.',
    category: 'fundraising',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'accountant', label: 'Recorded by', example: 'Miriam Fine' },
      V.amount,
      { token: 'plan', label: 'Target plan', example: 'FY26 Campaign' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Pledge Received',
        body: '{{accountant}} recorded {{amount|currency}} received toward a pledge for {{plan}}.',
      },
      email: {
        subject: 'Pledge Received',
        body: emailBody('Pledge Received', '{{accountant}} recorded {{amount|currency}} received toward a pledge for {{plan}}.'),
      },
    },
  },
  {
    key: 'pledge_received_recorded',
    label: 'Pledge Payment Received (creator)',
    description: 'Sent to the pledge creator when a payment toward their pledge is recorded.',
    category: 'fundraising',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'accountant', label: 'Recorded by', example: 'Miriam Fine' },
      V.amount,
      { token: 'plan', label: 'Target plan', example: 'FY26 Campaign' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Pledge Payment Received',
        body: '{{accountant}} recorded {{amount|currency}} received toward your pledge for {{plan}}.',
      },
      email: {
        subject: 'Pledge Payment Received',
        body: emailBody('Pledge Payment Received', '{{accountant}} recorded {{amount|currency}} received toward your pledge for {{plan}}.'),
      },
    },
  },

  // ── Jobs ───────────────────────────────────────────────────────
  {
    key: 'job_application_submitted',
    label: 'Job Application Submitted',
    description: 'Sent when someone applies to an open job posting.',
    category: 'jobs',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'applicant', label: 'Applicant', example: 'Baruch Adler' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New application: {{job_title}}',
        body: '{{applicant}} applied for "{{job_title}}".',
      },
      email: {
        subject: 'New application: {{job_title}}',
        body: emailBody('New application: {{job_title}}', '{{applicant}} applied for "{{job_title}}".', 'View Application'),
      },
    },
  },
  {
    key: 'job_created',
    label: 'Job Created',
    description: 'Sent when a new job posting is created.',
    category: 'jobs',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      { token: 'status_label', label: 'Status', example: 'Draft' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New job posting: {{job_title}}',
        body: 'A new job posting "{{job_title}}" was created ({{status_label}}).',
      },
      email: {
        subject: 'New job posting: {{job_title}}',
        body: emailBody('New job posting: {{job_title}}', 'A new job posting "{{job_title}}" was created ({{status_label}}).', 'View Job'),
      },
    },
  },
  {
    key: 'job_published',
    label: 'Job Published',
    description: 'Sent when a job posting goes live on the public jobs site.',
    category: 'jobs',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Job published: {{job_title}}',
        body: 'The job posting "{{job_title}}" is now live on the public jobs site.',
      },
      email: {
        subject: 'Job published: {{job_title}}',
        body: emailBody('Job published: {{job_title}}', 'The job posting "{{job_title}}" is now live on the public jobs site.', 'View Job'),
      },
    },
  },
  // NOTE: the applicant-message email (jobs/application-message-send.ts) is a
  // free-form message the sender composes each time and is delivered to an
  // *external* applicant without the internal app footer, so it is not a
  // configurable template — it stays on its dedicated builder.
  {
    key: 'onboarding_requested',
    label: 'Onboarding Requested',
    description: 'Sent to the hiring manager when HR asks them to fill in a new hire\'s onboarding details.',
    category: 'jobs',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'candidate', label: 'Candidate', example: 'Baruch Adler' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      { token: 'requested_by', label: 'Requested by', example: 'Leah Katz' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Onboarding needed: {{candidate}}',
        body: '{{requested_by}} asked you to complete onboarding details for {{candidate}} ({{job_title}}).',
      },
      email: {
        subject: 'Onboarding needed: {{candidate}}',
        body: emailBody(
          'Complete onboarding for {{candidate}}',
          '{{requested_by}} asked you to fill in the new-hire details for {{candidate}} ({{job_title}}). Some information is prefilled — please review, complete, and submit it for HR.',
          'Complete Onboarding',
        ),
      },
    },
  },
  {
    key: 'onboarding_submitted',
    label: 'Onboarding Details Submitted',
    description: 'Sent to HR when a hiring manager submits a new hire\'s onboarding details for review.',
    category: 'jobs',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'candidate', label: 'Candidate', example: 'Baruch Adler' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      { token: 'submitted_by', label: 'Submitted by', example: 'Mendel Cohen' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Onboarding ready to review: {{candidate}}',
        body: '{{submitted_by}} submitted onboarding details for {{candidate}} ({{job_title}}).',
      },
      email: {
        subject: 'Onboarding ready to review: {{candidate}}',
        body: emailBody(
          'Onboarding details submitted: {{candidate}}',
          '{{submitted_by}} submitted the onboarding details for {{candidate}} ({{job_title}}). Please review and approve to create the new employee.',
          'Review Onboarding',
        ),
      },
    },
  },

  // ── Projects ───────────────────────────────────────────────────
  {
    key: 'project_created',
    label: 'Project Created',
    description: 'Sent when a new project is created.',
    category: 'projects',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'body_text', label: 'Project summary', description: 'Generated summary of the new project.', example: 'Leah Katz created project "Summer Camp 2026" in Education.' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'New Project Created',
        body: '{{body_text}}',
      },
      email: {
        subject: 'New Project Created',
        body: emailBody('New Project Created', '{{body_text}}', 'View Project'),
      },
    },
  },

  // ── Vendors ────────────────────────────────────────────────────
  {
    key: 'vendor_update_by_expense',
    label: 'Vendor Updated by Expense',
    description: 'Sent when a vendor record is changed while entering an expense.',
    category: 'vendors',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'vendor_label', label: 'Vendor', example: 'Staples Inc.' },
      { token: 'changes', label: 'Changed fields', description: 'Description of what changed.', example: 'Address updated; W-9 attached' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Vendor updated by expense: {{vendor_label}}',
        body: '{{changes}}',
      },
      email: {
        subject: 'Vendor updated by expense: {{vendor_label}}',
        body: emailBody('Vendor updated by expense: {{vendor_label}}', '{{changes}}', 'View Vendor'),
      },
    },
  },

  // ── Employees (time-based reminders — never include finance info) ──
  {
    key: 'employee_birthday_reminder',
    label: 'Employee Birthday Reminder',
    description: "Sent 10 days before an employee's Hebrew birthday to their direct + indirect manager and the global notification group.",
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      { token: 'hebrew_birthday', label: 'Hebrew birthday', description: 'Hebrew day + month.', example: '15 Shevat' },
      { token: 'birthday_date', label: 'Date (this year)', format: 'date', example: '2026-02-02' },
      { token: 'days_until', label: 'Days until', format: 'number', example: '10' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Upcoming birthday: {{employee_name}} ({{hebrew_birthday}})',
        body: "{{employee_name}}'s Hebrew birthday ({{hebrew_birthday}}) is coming up on {{birthday_date|date}} — in {{days_until}} days.",
      },
      email: {
        subject: 'Upcoming birthday: {{employee_name}} ({{hebrew_birthday}})',
        body: emailBody(
          'Upcoming Employee Birthday',
          "{{employee_name}}'s Hebrew birthday ({{hebrew_birthday}}) is coming up on {{birthday_date|date}} — in {{days_until}} days.",
          'View Employee',
        ),
      },
    },
  },
  {
    key: 'employee_agreement_expiring',
    label: 'Employee Agreement Expiring',
    description: "Sent as an employee's agreement approaches expiry with no follow-up agreement, to their direct + indirect manager and the global notification group.",
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      { token: 'expiry_date', label: 'Expiry date', format: 'date', example: '2026-08-30' },
      { token: 'days_until', label: 'Days until expiry', format: 'number', example: '45' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Agreement expiring: {{employee_name}}',
        body: "{{employee_name}}'s agreement expires on {{expiry_date|date}} — in {{days_until}} days. After it expires they will not be able to work in the management system or report their time, and no follow-up agreement is on file.",
      },
      email: {
        subject: 'Agreement expiring in {{days_until}} days: {{employee_name}}',
        body: emailBody(
          'Employee Agreement Expiring',
          "{{employee_name}}'s agreement expires on {{expiry_date|date}} — in {{days_until}} days. After it expires they will not be able to work in the management system or report their time, and no follow-up agreement is on file. Please arrange a renewal if they should continue.",
          'View Employee',
        ),
      },
    },
  },
  // ── Employee contract changes (intentionally include full contract detail) ──
  {
    key: 'employee_agreement_changed',
    label: 'Employee Contract Added / Changed',
    description: 'Sent to the configured notification group when a new employee is onboarded or an existing employee\'s contract is added or changed. Includes the full contract detail and a link to the employee.',
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      { token: 'action', label: 'Action', description: 'What happened — "added" or "updated".', example: 'added' },
      { token: 'actor_name', label: 'Actor', description: 'Name of the user who made the change.', example: 'Chana Weiss' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      V.dept,
      { token: 'agreement_type', label: 'Agreement type', example: 'Fixed' },
      { token: 'pay_period', label: 'Pay period', example: 'Monthly' },
      V.amount,
      { token: 'hours_per_week', label: 'Expected hours/week', format: 'number', example: '40' },
      { token: 'start_date', label: 'Start date', format: 'date', example: '2026-08-01' },
      { token: 'end_date', label: 'End date', format: 'date', example: '2027-07-31' },
      V.link,
      { token: 'details_table', label: 'Contract details table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Contract {{action}}: {{employee_name}}',
        body: '{{actor_name}} {{action}} a contract for {{employee_name}} ({{job_title||—}}) — {{agreement_type}} {{amount|currency}}, starting {{start_date|date}}.',
      },
      email: {
        subject: 'Employee contract {{action}}: {{employee_name}}',
        body: `${H2('Employee Contract {{action}}')}\n${PARA('{{actor_name}} {{action}} a contract for <strong>{{employee_name}}</strong>.')}\n{{block:details_table}}\n${BTN('View Employee')}`,
      },
    },
  },

  {
    key: 'employee_terminated',
    label: 'Employee Terminated',
    description: "Sent to the configured notification group when an employee's status is changed to terminated. Their app login is disabled, and the message reports whether their Google Workspace account was suspended (via {{workspace_status}}).",
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      { token: 'actor_name', label: 'Actor', description: 'Name of the user who terminated the employee.', example: 'Chana Weiss' },
      { token: 'job_title', label: 'Job title', example: 'Program Coordinator' },
      V.dept,
      { token: 'work_email', label: 'Work email', example: 'dovid@merkos302.com' },
      { token: 'workspace_status', label: 'Workspace suspend status', description: 'Truthful sentence about the Google Workspace suspension outcome (suspended, could not be suspended, or blank when the integration is not configured).', example: ' Their Google Workspace account (dovid@merkos302.com) has been suspended.' },
      { token: 'termination_date', label: 'Termination date', format: 'date', example: '2026-08-28' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Employee terminated: {{employee_name}}',
        body: '{{actor_name}} changed {{employee_name}} ({{job_title||—}}) to terminated on {{termination_date|date}}. Their login has been disabled.{{workspace_status}}',
      },
      email: {
        subject: 'Employee terminated: {{employee_name}}',
        body: emailBody(
          'Employee Terminated',
          '{{actor_name}} changed <strong>{{employee_name}}</strong> ({{job_title||—}}, {{department||no department}}) to terminated on {{termination_date|date}}. Their app login has been disabled.{{workspace_status}}',
          'View Employee',
        ),
      },
    },
  },

  // ── Employee goals & KPI (MT-1419) ─────────────────────────────
  {
    key: 'kpi_sign_off_requested',
    label: 'KPI Sign-off Requested',
    description: 'Sent to the employee and their manager when a goals/KPI set is submitted for sign-off.',
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'set_name', label: 'KPI set name', example: 'FY2026 Goals' },
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      V.actor,
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'KPI sign-off requested: {{set_name}}',
        body: '{{actor_name}} requested sign-off on the goals/KPI set "{{set_name}}" for {{employee_name}}.',
      },
      email: {
        subject: 'KPI sign-off requested: {{set_name}}',
        body: emailBody(
          'KPI Sign-off Requested',
          '{{actor_name}} requested sign-off on the goals/KPI set "{{set_name}}" for {{employee_name}}. Please review and sign off.',
          'Review & Sign Off',
        ),
      },
    },
  },
  {
    key: 'kpi_set_signed_off',
    label: 'KPI Set Signed Off',
    description: 'Sent to the employee, their manager, and the set creator when a goals/KPI set is fully signed off.',
    category: 'employees',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'set_name', label: 'KPI set name', example: 'FY2026 Goals' },
      { token: 'employee_name', label: 'Employee', example: 'Dovid Green' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'KPI set signed off: {{set_name}}',
        body: 'The goals/KPI set "{{set_name}}" for {{employee_name}} has been fully signed off.',
      },
      email: {
        subject: 'KPI set signed off: {{set_name}}',
        body: emailBody(
          'KPI Set Signed Off',
          'The goals/KPI set "{{set_name}}" for {{employee_name}} has been signed off by both the employee and their manager.',
        ),
      },
    },
  },

  // ── Security ───────────────────────────────────────────────────
  {
    key: 'unauthorized_login_attempt',
    label: 'Unauthorized Login Attempt',
    description: 'Security alert sent when an unauthorized account tries to sign in.',
    category: 'security',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'email', label: 'Attempted email', example: 'stranger@example.com' },
      { token: 'reason', label: 'Reason', example: 'Email not registered (not in the system)' },
      { token: 'detail_table', label: 'Detail table', block: true },
    ],
    defaults: {
      in_app: {
        subject: 'Unauthorized login attempt: {{email}}',
        body: '{{reason}} — {{email}}',
      },
      email: {
        subject: '[Security] Unauthorized login attempt: {{email}}',
        body: `<h2 style="margin:0 0 12px;font-size:18px;color:#dc2626;">&#x26A0; Unauthorized Login Attempt</h2>\n<p style="margin:0 0 16px;font-size:14px;color:#6b7280;">Someone tried to log in with an account that is not authorized.</p>\n{{block:detail_table}}`,
      },
    },
  },

  // ── System ─────────────────────────────────────────────────────
  {
    key: 'cc_auto_match',
    label: 'Credit-Card Auto-Match',
    description: 'Sent when the credit-card auto-matcher finds potential matches.',
    category: 'system',
    channels: [IN_APP],
    variables: [
      { token: 'match_count', label: 'Match count', format: 'number', example: '2' },
      { token: 'transaction_count', label: 'Transactions matched', format: 'number', example: '2' },
      { token: 'auto_confirmed_count', label: 'Confirmed automatically', format: 'number', example: '0' },
      { token: 'pending_count', label: 'Waiting in Match Review', format: 'number', example: '2' },
    ],
    defaults: {
      in_app: {
        subject: 'CC Auto-Match: {{match_count}} potential match(es) found',
        body: 'Auto-matching found {{match_count}} new match(es) across {{transaction_count}} credit card transaction(s): {{pending_count}} waiting in Match Review, {{auto_confirmed_count}} confirmed automatically.',
      },
    },
  },
  {
    key: 'system_alert',
    label: 'System Alert',
    description: 'General system alert (e.g. a scheduled job reported failures).',
    category: 'system',
    channels: [IN_APP],
    variables: [
      { token: 'title_text', label: 'Alert title', example: 'Metric Scheduler: 1 of 5 queries failed' },
      { token: 'detail', label: 'Detail', example: 'Query "Weekly leads" timed out.' },
    ],
    defaults: {
      in_app: {
        subject: '{{title_text}}',
        body: '{{detail}}',
      },
    },
  },
  {
    key: 'backup_failed',
    label: 'Backup Failed',
    description: 'Sent to the backup notification group when a scheduled or manual backup run fails.',
    category: 'system',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'store_type', label: 'Store type', description: 'What failed to back up (database or files).', example: 'database' },
      { token: 'detail', label: 'Failure detail', description: 'The error message from the failed run.', example: 'connection timed out' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Backup failed ({{store_type}})',
        body: 'A {{store_type}} backup run failed: {{detail}}',
      },
      email: {
        subject: 'Backup failed ({{store_type}})',
        body: emailBody(
          'Backup failed',
          'A {{store_type}} backup run failed: {{detail}}',
          'Open Backup & Recovery',
        ),
      },
    },
  },
  {
    key: 'log_alert_triggered',
    label: 'Log Alert Triggered',
    description: 'Sent to the log alert group when a log monitor alert rule matches at least its threshold of records within its window.',
    category: 'system',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'rule_name', label: 'Rule name', example: 'Expense export failures' },
      { token: 'match_count', label: 'Matching records', description: 'How many records matched (a lower bound when the search was capped).', example: '12' },
      { token: 'threshold', label: 'Threshold', example: '5' },
      { token: 'window_minutes', label: 'Window (minutes)', example: '60' },
      { token: 'description', label: 'Rule description', example: 'The expense export keeps failing for the same user.' },
      { token: 'sample_event', label: 'Latest event', example: 'expense.export_failed' },
      { token: 'sample_message', label: 'Latest message', example: 'Timed out talking to QuickBooks' },
      { token: 'project', label: 'Project', example: 'Merkos Management' },
      { token: 'environment', label: 'Environment', example: 'production' },
      { token: 'deployed_at', label: 'Last deployed', description: 'When this environment was last deployed (New York time).', example: 'Oct 16, 2026, 2:05 PM EDT' },
      { token: 'deployment', label: 'Project, environment and last deploy (table)', block: true },
      { token: 'analysis', label: 'AI analysis — how to resolve and avoid it (empty when off or unavailable)', block: true },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Log alert: {{rule_name}} ({{project}} · {{environment}})',
        body: '{{match_count}} matching log records in the last {{window_minutes}} minutes (threshold {{threshold}}) on {{project}} · {{environment}}. Latest: {{sample_event}} — {{sample_message||no message}}',
      },
      email: {
        subject: '[{{project}} · {{environment}}] Log alert: {{rule_name}} ({{match_count}} in {{window_minutes}} min)',
        body: `{{block:deployment}}\n${H2('Log alert: {{rule_name}}')}\n${PARA('{{match_count}} log records matched in the last {{window_minutes}} minutes (threshold {{threshold}}).')}\n${PARA('{{description}}')}\n${PARA('Latest: <strong>{{sample_event}}</strong> — {{sample_message||no message}}')}\n{{block:analysis}}\n${BTN('Open Monitor')}`,
      },
    },
  },
  {
    key: 'log_daily_summary',
    label: 'Daily Log Summary',
    description: 'Daily email/in-app summary of the last 24 hours of errors and warnings, by error type, user and function, plus the alert rules that fired.',
    category: 'system',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'period', label: 'Period', example: 'Oct 13, 8:00 AM – Oct 14, 8:00 AM' },
      { token: 'error_count', label: 'Errors', example: '42' },
      { token: 'warning_count', label: 'Warnings', example: '17' },
      { token: 'alert_count', label: 'Alerts fired', example: '2' },
      { token: 'tables', label: 'Summary tables', block: true },
      { token: 'project', label: 'Project', example: 'Merkos Management' },
      { token: 'environment', label: 'Environment', example: 'production' },
      { token: 'deployed_at', label: 'Last deployed', description: 'When this environment was last deployed (New York time).', example: 'Oct 16, 2026, 2:05 PM EDT' },
      { token: 'deployment', label: 'Project, environment and last deploy (table)', block: true },
      { token: 'analysis', label: 'AI analysis — how to resolve and avoid it (empty when off or unavailable)', block: true },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Daily log summary ({{project}} · {{environment}})',
        body: '{{error_count}} errors and {{warning_count}} warnings in the last 24 hours on {{project}} · {{environment}}; {{alert_count}} alert rule(s) fired.',
      },
      email: {
        subject: '[{{project}} · {{environment}}] Daily log summary: {{error_count}} errors, {{warning_count}} warnings',
        body: `{{block:deployment}}\n<h2 style="margin:0 0 4px;font-size:18px;">Daily log summary</h2>\n<p style="margin:0 0 16px;font-size:13px;color:#9ca3af;">{{period}}</p>\n${PARA('{{error_count}} errors and {{warning_count}} warnings; {{alert_count}} alert rule(s) fired.')}\n{{block:analysis}}\n{{block:tables}}\n${BTN('Open Monitor')}`,
      },
    },
  },

  // ── Reports ────────────────────────────────────────────────────
  {
    key: 'report_digest',
    label: 'Scheduled Report Digest',
    description: 'Weekly/monthly report digest email sent to a notification group.',
    category: 'reports',
    channels: [EMAIL],
    variables: [
      { token: 'title', label: 'Report title', example: 'Expenses — Weekly Report' },
      { token: 'period', label: 'Period', example: 'Jul 7 – Jul 13, 2026' },
      { token: 'summary', label: 'Summary lines', block: true },
      { token: 'tables', label: 'Report tables', block: true },
      { token: 'cta', label: 'Call-to-action button', block: true },
    ],
    defaults: {
      email: {
        subject: '{{title}} ({{period}})',
        body: `<h2 style="margin:0 0 4px;font-size:18px;">{{title}}</h2>\n<p style="margin:0 0 16px;font-size:13px;color:#9ca3af;">{{period}}</p>\n{{block:summary}}\n{{block:tables}}\n{{block:cta}}`,
      },
    },
  },
  // ── Office / desk booking (Phase 3) ──────────────────────────────
  {
    key: 'desk_booking_requested',
    label: 'Desk Booking Requested',
    description: 'Sent to the approver group when an employee requests a desk booking.',
    category: 'office',
    channels: [IN_APP, EMAIL],
    variables: [
      V.actor,
      { token: 'desk', label: 'Desk', description: 'Desk label and location.', example: 'Desk 3 · 788 Eastern Pkwy · Floor 3 · Room 302' },
      { token: 'when', label: 'When', description: 'The requested time (schedule and/or dates).', example: 'Mon 09:00–17:00' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Desk booking request',
        body: '{{actor_name}} requested {{desk}} ({{when}}).',
      },
      email: {
        subject: 'Desk booking request from {{actor_name}}',
        body: emailBody('Desk booking request', '{{actor_name}} requested to book {{desk}} for {{when}}. Review and approve or reject the request.', 'Review Request'),
      },
    },
  },
  {
    key: 'desk_booking_approved',
    label: 'Desk Booking Approved',
    description: 'Sent to the requester when their desk booking is approved.',
    category: 'office',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'desk', label: 'Desk', example: 'Desk 3 · 788 Eastern Pkwy · Floor 3 · Room 302' },
      { token: 'when', label: 'When', example: 'Mon 09:00–17:00' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Desk booking approved',
        body: 'Your booking for {{desk}} ({{when}}) has been approved.',
      },
      email: {
        subject: 'Desk booking approved',
        body: emailBody('Desk booking approved', 'Your booking for {{desk}} ({{when}}) has been approved.'),
      },
    },
  },
  {
    key: 'desk_booking_rejected',
    label: 'Desk Booking Rejected',
    description: 'Sent to the requester when their desk booking is rejected.',
    category: 'office',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'desk', label: 'Desk', example: 'Desk 3 · 788 Eastern Pkwy · Floor 3 · Room 302' },
      { token: 'note_suffix', label: 'Note line', description: 'Reviewer note, prefixed with " Note:" (blank when none).', example: ' Note: Reserved for a visitor' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Desk booking rejected',
        body: 'Your booking for {{desk}} has been rejected.{{note_suffix}}',
      },
      email: {
        subject: 'Desk booking rejected',
        body: emailBody('Desk booking rejected', 'Your booking for {{desk}} has been rejected.{{note_suffix}}'),
      },
    },
  },
  // ── Grant suggestions (MT-1662) ──────────────────────────────────
  {
    key: 'grant_suggestion_requested',
    label: 'Grant Suggestion Requested',
    description: 'Sent to the routed group (or admin/accounting) when an employee suggests a grant to an institution.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      V.actor,
      { token: 'institution', label: 'Institution', description: 'The institution (vendor) the grant is suggested for.', example: 'Chabad of Springfield' },
      V.amount,
      { token: 'frequency', label: 'Frequency', description: 'One time or Monthly.', example: 'Monthly' },
      { token: 'total_amount', label: 'Total amount', format: 'currency', description: 'Total of the whole schedule.', example: '6000' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Grant suggestion from {{actor_name}}',
        body: '{{actor_name}} suggested a {{frequency}} grant of {{amount|currency}} to {{institution}} (total {{total_amount|currency}}).',
      },
      email: {
        subject: 'Grant suggestion from {{actor_name}}',
        body: emailBody('New grant suggestion', '{{actor_name}} suggested a {{frequency}} grant of {{amount|currency}} to {{institution}} (total {{total_amount|currency}}). Review and approve or reject the request.', 'Review Request'),
      },
    },
  },
  {
    key: 'grant_suggestion_approved',
    label: 'Grant Suggestion Approved',
    description: 'Sent to the employee when their grant suggestion is approved.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'institution', label: 'Institution', example: 'Chabad of Springfield' },
      { token: 'total_amount', label: 'Total amount', format: 'currency', example: '6000' },
      { token: 'decision_note', label: 'Decision note', description: 'The reviewer\'s note (blank when none).', example: 'Approved for this fiscal year' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Grant suggestion approved',
        body: 'Your grant suggestion to {{institution}} ({{total_amount|currency}}) has been approved. {{decision_note}}',
      },
      email: {
        subject: 'Grant suggestion approved',
        body: emailBody('Grant suggestion approved', 'Your grant suggestion to {{institution}} ({{total_amount|currency}}) has been approved. {{decision_note}}'),
      },
    },
  },
  {
    key: 'grant_suggestion_rejected',
    label: 'Grant Suggestion Rejected',
    description: 'Sent to the employee when their grant suggestion is rejected.',
    category: 'expenses',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'institution', label: 'Institution', example: 'Chabad of Springfield' },
      { token: 'total_amount', label: 'Total amount', format: 'currency', example: '6000' },
      { token: 'decision_note', label: 'Decision note', description: 'The reviewer\'s note (blank when none).', example: 'Outside this year\'s budget' },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Grant suggestion rejected',
        body: 'Your grant suggestion to {{institution}} ({{total_amount|currency}}) has been rejected. {{decision_note}}',
      },
      email: {
        subject: 'Grant suggestion rejected',
        body: emailBody('Grant suggestion rejected', 'Your grant suggestion to {{institution}} ({{total_amount|currency}}) has been rejected. {{decision_note}}'),
      },
    },
  },
  // ── Custom dashboards (MT-1666) ──────────────────────────────────
  {
    key: 'dashboard_delivery',
    label: 'Scheduled Dashboard Delivery',
    description: 'Sent to each recipient of a dashboard delivery schedule. The email renders the dashboard and attaches it as a PDF.',
    category: 'reports',
    channels: [IN_APP, EMAIL],
    variables: [
      { token: 'dashboard_name', label: 'Dashboard name', example: 'Fundraising KPIs' },
      { token: 'cadence', label: 'Cadence', description: 'Daily, Weekly or Monthly.', example: 'Weekly' },
      { token: 'date', label: 'Delivery date', format: 'date', example: '2026-10-14' },
      { token: 'dashboard', label: 'Dashboard content', description: 'The dashboard rendered as email HTML.', block: true },
      V.link,
    ],
    defaults: {
      in_app: {
        subject: 'Dashboard: {{dashboard_name}}',
        body: 'Your {{cadence}} delivery of the "{{dashboard_name}}" dashboard is ready.',
      },
      email: {
        subject: '{{dashboard_name}} — {{date|date}}',
        body: `<h2 style="margin:0 0 4px;font-size:18px;">{{dashboard_name}}</h2>\n<p style="margin:0 0 16px;font-size:13px;color:#9ca3af;">{{cadence}} delivery · {{date|date}} · PDF attached</p>\n{{block:dashboard}}\n${BTN('Open Dashboard')}`,
      },
    },
  },
];

// ── Lookup helpers ───────────────────────────────────────────────

const BY_KEY = new Map(NOTIFICATION_TEMPLATES.map((t) => [t.key, t]));

export function getTemplateDef(key: string): NotificationTemplateDef | undefined {
  return BY_KEY.get(key);
}

/** All (key, channel) pairs a template supports — used for seeding. */
export function allTemplateChannels(): Array<{ key: string; channel: NotificationChannel }> {
  const out: Array<{ key: string; channel: NotificationChannel }> = [];
  for (const t of NOTIFICATION_TEMPLATES) {
    for (const ch of t.channels) out.push({ key: t.key, channel: ch });
  }
  return out;
}

/** Distinct categories in registry order. */
export function templateCategories(): string[] {
  const seen: string[] = [];
  for (const t of NOTIFICATION_TEMPLATES) {
    if (!seen.includes(t.category)) seen.push(t.category);
  }
  return seen;
}

/**
 * The entity type each notification template relates to. Kept here (with the
 * registry, the source of truth) so the admin UI can filter templates by it
 * without a redundant DB column. Values mirror shared `EntityType` where one
 * exists, plus a few notification-only kinds (security/system/report).
 */
const ENTITY_TYPE_BY_KEY: Record<string, string> = {
  expense_approved: 'expense',
  expense_rejected: 'expense',
  expense_paid: 'expense',
  expense_request: 'expense',
  expense_submitted: 'expense',
  payee_expense_paid: 'expense',
  payee_expense_paid_check_pickup: 'expense',
  payee_expense_paid_check_mail: 'expense',
  payee_expense_paid_transfer: 'expense',
  payee_expense_paid_paypal: 'expense',
  payee_expense_paid_remittance: 'expense',
  payee_expense_paid_payroll: 'expense',
  budget_submitted: 'budget',
  budget_approved: 'budget',
  budget_rejected: 'budget',
  budget_returned_to_draft: 'budget',
  subscription_request: 'subscription',
  subscription_submitted: 'subscription',
  thread_mention: 'thread',
  thread_status_changed: 'thread',
  feedback_submitted: 'thread',
  time_warning: 'time_entry',
  time_edited: 'time_entry',
  time_submitted: 'time_entry',
  payroll_reminder: 'payroll',
  lead_created: 'lead',
  pledge_created: 'pledge',
  pledge_received: 'pledge',
  pledge_received_recorded: 'pledge',
  job_application_submitted: 'job',
  job_created: 'job',
  job_published: 'job',
  onboarding_requested: 'employee_onboarding',
  onboarding_submitted: 'employee_onboarding',
  project_created: 'project',
  vendor_update_by_expense: 'vendor',
  employee_birthday_reminder: 'employee',
  employee_agreement_expiring: 'employee',
  employee_agreement_changed: 'employee',
  employee_terminated: 'employee',
  remittance_paid: 'remittance',
  remittance_failed: 'remittance',
  remittance_batch_completed: 'remittance_batch',
  unauthorized_login_attempt: 'security',
  cc_auto_match: 'credit_card',
  system_alert: 'system',
  log_alert_triggered: 'system',
  log_daily_summary: 'system',
  report_digest: 'report',
  desk_booking_requested: 'desk_booking',
  desk_booking_approved: 'desk_booking',
  desk_booking_rejected: 'desk_booking',
  grant_suggestion_requested: 'grant_suggestion',
  grant_suggestion_approved: 'grant_suggestion',
  grant_suggestion_rejected: 'grant_suggestion',
  dashboard_delivery: 'dashboard',
};

/** Entity type for a template key (falls back to 'other'). */
export function getTemplateEntityType(key: string): string {
  return ENTITY_TYPE_BY_KEY[key] ?? 'other';
}

/** Distinct entity types present in the registry, in first-seen order. */
export function templateEntityTypes(): string[] {
  const seen: string[] = [];
  for (const t of NOTIFICATION_TEMPLATES) {
    const et = getTemplateEntityType(t.key);
    if (!seen.includes(et)) seen.push(et);
  }
  return seen;
}
